<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="auto-qa-agents-page" element-loading-text="Loading...">
    <div>
      <div class="button-flex">
        <i v-if="teamId" class="el-icon-back" @click="goBackToGroupsTeamsPage"></i>
        <i v-else class="el-icon-back" @click="goBackToHomePage"></i>
        <span v-if="teamId" class="go-back-section" @click="goBackToGroupsTeamsPage">Back to {{ this.teamId==='all' ? 'All Teams' : agentTeamObj.label + ' Team' }}</span>
        <span v-else class="go-back-section" @click="goBackToHomePage">Back to home</span>

        <div class="auto-qa-agents-page-title" v-if="agent&&agent.displayName">{{ agent.displayName }}</div>
      </div>
      <div class="wy-page-tab">
        <div class="main-section" v-if="!noData">
          <div class="chart-section">
            <wy-auto-qa-panel-dynamic-chart ref="dynamicChart" :controller="module" :lineChartTitleMapper="lineChartTitleMapper" :waterfallChartFooterMapper="waterfallChartFooterMapper" :legends="lineChartLegends" />
          </div>
          <div class="table-title">Contacts</div>
          <div class="table-section" style="overflow: hidden;" v-loading="tableLoading">
            <wy-table-list ref="table"  :controller="module" :enableSelection="true" :noMainColumn="true" :defaultSort="{ name: sortBy, type: sortType }" :rowClickEvent="handleRowClick" :paginationSorting="paginationSorting" searchFieldName="contact reaons" :isHeaderTooltipVisible="true"/>
          </div>
          <wy-table-pagination v-if="hasPagination" :callback="pageChange" :isTableLoading="tableLoading"></wy-table-pagination>
        </div>
        <div v-else class="no-data">
          <div class="content">No data available yet for this selection.</div>
          <div class="content">Please use the global filter to make another selection with available data.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import wyHomeExperienceAutomationPanelDynamicChart from './panel-templates/v2/wy-auto-qa-panel-dynamic-chart.vue';
import WyTableList from '../widgets/wy-table-list';
import WyTablePagination from '../widgets/wy-table-pagination';
import WyConstants from '../../library/wy-constants';

  export default Vue.extend({
    name: 'wy-auto-qa-agents-page',
    components: {
      'wy-auto-qa-panel-dynamic-chart': wyHomeExperienceAutomationPanelDynamicChart,
      'wy-table-list': WyTableList,
      'wy-table-pagination': WyTablePagination
    },
    props: ['agent', 'teamId'],
    data() {
      return {
        module: this,
        noData: false,
        tableData: null,
        dynamicChartsLoading: true,
        loading: false,
        tableLoading: false,
        lineChartLegends: [],
        lineChartTitleMapper: {
          autoEvalScore: 'Evaluation score trend'
        },
        waterfallChartFooterMapper: {
          autoEvalScore: 'Auto evaluation sections'
        },
        selectedAgent: '',
        sortBy: 'overallScore',
        sortType: 1,
        columns: [],
      }
    },

    computed: {
      ...mapGetters(['dataModel', 'tableModel', 'filterChangedStatus', 'tokenInfo']),

      hasPagination() {
        // console.log(this.dataModel.page.pagination, this.dataModel.page.size, this.tableModel.totalRecs)
        return this.dataModel.page.pagination && this.dataModel.page.size < this.tableModel.totalRecs
      },
      disableTab() { 
        return this.loading||!this.$refs.groups||!this.$refs.teams||this.$refs.groups&&this.$refs.groups.dynamicChartsLoading&&this.$refs.groups.tableLoading
        ||this.$refs.teams&&this.$refs.teams.dynamicChartsLoading&&this.$refs.teams.tableLoading
      },

      isFilterChanged() {
        return JSON.stringify(this.cloneFilters) !== JSON.stringify(this.dataModel.filters)
      },

      agentTeamObj() {
        let agentTeam = {label: 'All Teams'};
        if(this.dataModel.filters.agents && this.dataModel.filters.agents.dataSource) {
          this.dataModel.filters.agents.dataSource.forEach(agent => {
            if(agent.key === this.agent.id) {
              this.dataModel.filters.agentTeams.dataSource.forEach(team => {
                if(team.key === this.teamId) {
                  agentTeam = team
                }
              })
            }
          })
        }
        return agentTeam;
      }
    },

    methods: {
      ...mapMutations(['updateDataModelColumns', 'updateDataModelPagination', 'updateDataModelPageSize', 'updateDataModelPageNumber']),
      ...mapActions(['fetchAutoQAEvalGroupTeamLineChart', 'fetchAutoQAEvalGroupTeamBarChart', 'fetchAutoQAEvalAgentTableSummary', 'fetchAutoQAEvalPageFilters']),

      async handleFilterChange() {
        if(this.agent) {
          let options = {
            agents: [this.agent.id]
          }
          const res = await this.fetchAutoQAEvalPageFilters(options)
          const hasAgents = (res.data.agentGroups.length > 0) ? true : false
          if(hasAgents) {
            this.noData = false;
            await this.handleLoadData();
          }
          else {
            this.noData = true;
          }
        }
        else {
          this.goBackToGroupsTeamsPage()
        }
      },

      async pageChange(pageNumber) {
        this.updateDataModelPageNumber(pageNumber)
        let options = this.prepareOptions()
        this.tableLoading = true;
        await this.fetchAutoQAEvalAgentTableSummary(options);
        this.updateDataModelColumns({ columns: this.columns });
        this.$refs.table && this.$refs.table.refreshTable({ disablePageLoading: true });
        this.tableLoading = false;
      },

      async paginationSorting({ sortBy, sortType }) {
        // console.log(sortBy, sortable, sortType)
        this.sortBy = sortBy;
        this.sortType = sortType
        this.pageChange(1)
      },

      handleRowClick(index, item) {
        if(this.tokenInfo && this.tokenInfo.authHost) {
          if(this.dataModel.filters && this.dataModel.filters.formId && this.dataModel.filters.formId.value) {
            window.localStorage.setItem(WyConstants.MEDIAPLAYER_AUTOQA_SELECTED_FORMID, this.dataModel.filters.formId.value);
            window.localStorage.setItem(WyConstants.MEDIAPLAYER_AUTOQA_SELECTED_FORMID_FOR_SUMMARY, this.dataModel.filters.formId.value);
          }
          const mediaLink = `https://${this.tokenInfo.authHost}/#/recordings/${item.contactId}/ccr/player?aqFormId=${this.dataModel.filters.formId.value}`
          window.open(mediaLink);
        }
      },

      prepareOptions() {
        let options = {
          type: 'agents',
          value: [this.agent.id],
          sortColumn: this.sortBy,
          sorting: this.sortType ? 'desc' : 'asc',
          pageSize: this.dataModel.page.size,
          pageNumber: this.dataModel.page.number
        }
        return options;
      },

      async handleLoadData() {
        // let options = this.prepareOptions()
        let options = {
          type: 'agents',
          apiType: 'agent',
          displayName: this.agent.displayName,
          value: [this.agent.id]
        }
        this.dynamicChartsLoading = true;
        this.tableLoading = true;
        this.noData = false;
        let groupData = await this.fetchAutoQAEvalGroupTeamLineChart(options);
        if(groupData) {
          this.lineChartLegends = [options.displayName, this.agentTeamObj.label + ' Team'];
          this.renderLineChart(groupData, this.$refs.dynamicChart)
        }
        const barData = await this.fetchAutoQAEvalGroupTeamBarChart(options);
        this.renderBarChart(barData, this.$refs.dynamicChart)
        this.dynamicChartsLoading = false;
        await this.fetchAgentTableSummary();
      },

      generateColumns() {
        let sections = this.tableModel.sections;
        this.columns = [
        { label: 'Contact ID', name: 'contactId', mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Contact ID', align: 'left', minWidth: 383, maxWidth: 383, fixed: true, mainColumn: true, copyable: false, isEditTopicName: false, extendableField: true },
        { label: 'Date', name: 'dateDisplay', selected: true, sortable: true, hasSubtitle: false, group: 'Date', sortBy: 'date', minWidth: 180 , align: 'center'},
        { label: 'Call Handle Time', name: 'durationDisplay', selected: true, sortable: true, hasSubtitle: false, group: 'Call Handle Time', sortBy: 'duration',minWidth: 180 , align: 'center'},
        { label: 'AES', name: 'overallScoreDisplay', selected: true, sortable: true, hasSubtitle: false, group: 'Overall Score', sortBy: 'overallScore', minWidth: 100, suffix: '', align: 'center' },
        ];
        if(sections && sections.length > 0) {
          sections.forEach(section => {
            let obj = { 
              label: section, 
              name: (section.replace(/\s(.)/g, function (a) {
                return a.toUpperCase();
              })
              .replace(/\s/g, '')
              .replace(/^(.)/, function (b) {
                  return b.toLowerCase();
              })) + 'Display', 
              selected: true, 
              sortable: true, 
              hasSubtitle: false, 
              group: section, 
              sortBy: section, 
              minWidth: 120, 
              suffix: '', 
              align: 'center' 
            }
            this.columns.push(obj)
          })
        }
      },

      async fetchAgentTableSummary() {
        this.updateDataModelPageNumber(1)
        this.sortBy = 'overallScore'
        let options = this.prepareOptions()
        await this.fetchAutoQAEvalAgentTableSummary(options);
        this.noData = (this.tableModel.results.length == 0) ? true : false 
        this.generateColumns();
        this.updateDataModelColumns({ columns: this.columns });
        this.$refs.table && this.$refs.table.refreshTable({ disablePageLoading: true });
        this.tableLoading = false;
        this.fetchData = false;
      },

      renderLineChart(volumeData, dynamicChartRef) {
        const apiVolumeData = volumeData.autoEvalScore;
        if(dynamicChartRef) {
          if (apiVolumeData) {
            dynamicChartRef.showLineChart = true;
            dynamicChartRef.networkData.autoEvalScore = apiVolumeData;
            const noData =
              dynamicChartRef.networkData.autoEvalScore.dailyMetrics.length &&
              dynamicChartRef.networkData.autoEvalScore.dailyMetrics.every((metric) => metric.value == -1);

            if (noData) {
              dynamicChartRef.showLineChart = false;
            }
          }
          else {
            dynamicChartRef.showLineChart = false;
          }
        }
      },

      renderBarChart(barData, dynamicChartRef) {
        if(barData && barData.autoEvalSectionScore && dynamicChartRef) {
          dynamicChartRef.barChartHasData = true;
          const barDataExists = barData && Object.keys(barData) && Object.keys(barData).length;

          if (!barDataExists) {
            dynamicChartRef.barChartHasData = false;
          }

          if(!dynamicChartRef.networkData.autoEvalScore) {
            dynamicChartRef.networkData.autoEvalScore = {}
          }

          dynamicChartRef.networkData.autoEvalScore.barMetrics = {};
          
          if(Object.keys(barData.autoEvalSectionScore.barMetrics).length > 0) {
            Object.keys(barData.autoEvalSectionScore.barMetrics).forEach((item) => {
              if(barData.autoEvalSectionScore.barMetrics[item] != null && barData.autoEvalSectionScore.barMetrics[item] != '-1') {
                dynamicChartRef.networkData.autoEvalScore.barMetrics[item] = barData.autoEvalSectionScore.barMetrics[item]
              } else {
                // for -1 case, we need to show "N/A" as the bar value, but "hide" the bar
                dynamicChartRef.networkData.autoEvalScore.barMetrics[item] = '0.00001'; // workaround to show N/A
              }
            })
          }
          const noData = Object.values(dynamicChartRef.networkData.autoEvalScore.barMetrics).every((metric) => metric == -1);
          if (noData) dynamicChartRef.barChartHasData = false;
          dynamicChartRef.reloadBothPanels();
          this.handleRenderBarChart(dynamicChartRef, 'autoEvalScore');
        }
      },

      handleRenderBarChart(dynamicChartRef) {
        dynamicChartRef.showBarChart = true;
      },

      goBackToGroupsTeamsPage() {
        this.$router.push({ name: 'autoQAEvaluationGroupsTeams', params: { teamId: this.teamId, filterDisplayName: this.$router.history.current.params.filterDisplayName}});
      },

      goBackToHomePage() {
        this.$router.push({ name: 'autoQAHome'});
      }
    },

    async created() {
      this.$router.currPageComponent = this;
      this.initDate = new Date();
      // listen events from other components
      this.$root.registerPageEvent(this.$route.name, (event) => {
        if (this[event.name]) this[event.name]();
      });
      this.updateDataModelPagination(true)
      this.updateDataModelPageSize(10)
      if(this.agent) {
        this.handleLoadData();
      }
      else {
        this.goBackToGroupsTeamsPage()
      }
    },
  })

</script>

<style lang="scss">
  .auto-qa-agents-page {
    i.el-icon-back {
      cursor: pointer;
      font-size: 20px;
      font-weight: 500;
      position: relative;
      top: -16px;
      padding-right: 5px;
    }
    .go-back-section {
      cursor: pointer;
      font-size: 11.2px;
      font-weight: 400;
      position: relative;
      top: -20px;
      right: 0px;
    }
    .auto-qa-agents-page-title {
      color: var(--site-font-secondary-color);
      font-size: 25px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-weight: 600;
      min-width: 430px;
    }
    .table-title {
      font-size: 17.5px;
      font-style: normal;
      font-weight: 700;
      padding-bottom: 15px;
    }
    .chart-section {
      margin-top: 20px;
    }
    .table-section.el-loading-parent--relative {
      height: 400px;
    }
    .table-section {
      border: solid 1px #DFE4E6;
      border-radius: 5px;

      .wy-table-list {
        .wy-table-fixed tbody td:nth-last-child(1) {
          padding-right: 10px;
          text-decoration: underline;
          color: #006FE7;
        }
        thead {
          background-color: #FFF;
          .wy-sub-header th.lastChild {
            border-right-color: #FFF;
          }
        }
        tbody tr {
          td.last-child {
            border-right: 1px solid transparent;
            .el-no-data {
              justify-content: center !important;
            }
          }
          td div {
            justify-content: left;
          }
          
        }
        .wy-table-main {
          .wy-sub-header th {
            text-align: left !important;
          }
          .wy-sub-header th.lastChild {
            border-right-color: #FFF;
          }
          
          thead tr :first-child {
            padding-left: 5px;
          }
          thead th {
            background-color: #FFF;
          }
          tbody {
            height: fit-content;
            &::-webkit-scrollbar {
              width: 20px;
              height: 20px;
            }
            &::-webkit-scrollbar-thumb {
              border-radius: 180px;
              border: 5px solid transparent;
              border-top: 6px solid transparent;
              background-clip: content-box;
              background-color: #CAD2DA;
            }
            &::-webkit-scrollbar-track {
              background:#FFF;
              border-top: 1px solid #CAD2DA;
            }
            &::-webkit-scrollbar:vertical {
              width: 14px;
            }
            &::-webkit-scrollbar-thumb:vertical {
              border-radius: 160px;
              border: 2px solid transparent;
              border-left: 4px solid transparent;
              background-clip: content-box;
              background-color: #CAD2DA;
            }
            &::-webkit-scrollbar-track:vertical {
              border-left: 1px solid #CAD2DA;
              border-top: none !important;
            }
            tr td:first-child {
              padding-left: 12px;
            }
          }
        }
        .wy-table-tail {
          display: table-cell;
          min-width: 0px;
        }
      }
    }
    .no-data {
      padding-top: 50px;
      text-align: center;
      width: 100%;
      .content {
        padding-bottom: 5px;
        font-size: 17.5px;
        font-style: normal;
        font-weight: 400;
      }
    }
  }
  
</style>